import React from "react";
import { MEASURES } from "../../config/products";
import type { ActivityMeasure } from "../../config/products";
import { MapGeometry } from "../../services/mapsApi";
import { ODM_DIRECTIONS } from "../../utils/odmDirection";
import type { OdmDirection } from "../../utils/odmDirection";

interface AggregationConfigurationProps {
  selectedGrids: MapGeometry[];
  onGridRemove: (gridId: string) => void;
  /** What chooses the data window (the available-date picker). The page owns its state. */
  dataControl: React.ReactNode;
  /** The one measure this request returns, shown as plain text. Hidden when not given. */
  measureLabel?: string;
  /** With two or more of these (and `measure` and `onMeasureChange`) the measure is a picker instead. */
  measureOptions?: { value: ActivityMeasure; label: string }[];
  measure?: ActivityMeasure;
  onMeasureChange?: (m: ActivityMeasure) => void;
  /**
   * Origin-Destination only: how trips are counted for the selected areas, as a radio group at the
   * top. `unavailable` names the options that cannot be chosen, with the reason shown in place of
   * the helper text.
   */
  direction?: {
    value: OdmDirection;
    onChange: (d: OdmDirection) => void;
    unavailable?: Partial<Record<OdmDirection, string>>;
  };
}

const AggregationConfiguration: React.FC<AggregationConfigurationProps> = ({
  selectedGrids,
  onGridRemove,
  dataControl,
  measureLabel,
  measureOptions,
  measure,
  onMeasureChange,
  direction,
}) => {
  const pickMeasure = !!measureOptions && measureOptions.length >= 2 && !!measure && !!onMeasureChange;
  return (
    <div className="w-80 bg-base-100 border-l border-base-300 h-full flex flex-col">
      <div className="flex-1 overflow-y-auto p-4">
        <h2 className="text-lg font-semibold text-base-content mb-4">
          Aggregation Configuration
        </h2>

        {direction ? (
          <fieldset className="mb-4">
            <legend className="mb-1 block text-sm font-medium text-base-content">Direction</legend>
            <div className="space-y-2">
              {ODM_DIRECTIONS.map((d) => {
                const reason = direction.unavailable?.[d.value];
                return (
                  <label
                    key={d.value}
                    className={`flex items-start gap-2 ${reason ? "cursor-not-allowed opacity-60" : "cursor-pointer"}`}
                  >
                    <input
                      type="radio"
                      name="odm-direction"
                      className="radio radio-sm mt-0.5"
                      value={d.value}
                      checked={direction.value === d.value}
                      disabled={!!reason}
                      onChange={() => direction.onChange(d.value)}
                    />
                    <span>
                      <span className="block text-sm">{d.label}</span>
                      <span className="block text-xs text-ink-muted">{reason ?? d.help}</span>
                    </span>
                  </label>
                );
              })}
            </div>
          </fieldset>
        ) : null}

        {pickMeasure ? (
          <div className="mb-4">
            <label htmlFor="measure-select" className="mb-1 block text-sm text-ink-muted">
              Measure
            </label>
            <select
              id="measure-select"
              className="select select-bordered select-sm w-full"
              value={measure}
              onChange={(e) => onMeasureChange?.(e.target.value as ActivityMeasure)}
            >
              {measureOptions?.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
            {measure ? <p className="mt-1 text-sm text-ink-muted">{MEASURES[measure].definition}</p> : null}
          </div>
        ) : measureLabel ? (
          <div className="text-sm mb-4">
            <span className="text-ink-muted">Measure:</span> {measureLabel}
          </div>
        ) : null}

        {/* Delivery day and time resolution, from what has actually been delivered */}
        <div className="collapse collapse-arrow bg-base-200 mb-4">
          <input type="checkbox" defaultChecked />
          <div className="collapse-title text-sm font-medium">
            Data window
          </div>
          <div className="collapse-content">{dataControl}</div>
        </div>

        {/* Grid Selection Filter */}
        <div className="collapse collapse-arrow bg-base-200">
          <input type="checkbox" />
          <div className="collapse-title">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Selected Grids</span>
              {selectedGrids.length > 0 && (
                <div className="badge badge-success badge-sm">
                  {selectedGrids.length}
                </div>
              )}
            </div>
          </div>
          <div className="collapse-content">
            {selectedGrids.length === 0 ? (
              <div className="text-xs text-base-content/60">
                No grids selected. Click on map grids to select them.
              </div>
            ) : (
              <div className="space-y-2">
                <div className="max-h-48 overflow-y-auto">
                  {selectedGrids.map((grid) => (
                    <div
                      key={grid.geo_id}
                      className="bg-base-100 p-2 rounded"
                    >
                      <span className="text-xs font-mono text-base-content">
                        {grid.geo_id}
                      </span>
                    </div>
                  ))}
                </div>
                <button
                  className="btn btn-sm w-full btn-disabled"
                  disabled
                >
                  Edit Geometry
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AggregationConfiguration;
